import { dsvFormat } from 'd3';
import { t } from 'i18next';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { isValidNumber } from '../../../../../../../utils/math/number';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { addLineSegmentsToDesignNode } from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../../elements/design/design-node/utils/point-node';
import {
  AttributeData,
  AttributeId,
  SkyMapAttribute,
} from '../../../../../../elements/design/skymap-extras';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { Coordinates } from '../../../../../../utilities/math-helpers';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { getAttributeDataOrAddValue, getEnumAttributeOrCreate } from '../../utils/attributes';
import { repositionDesign } from '../../utils/re-position';

function parseCsv(content: string, options = { minColumns: 1 }) {
  const delimiters = ['\t', ';', ','];

  function parseUniformCsv(delimiter: string) {
    try {
      let result = dsvFormat(delimiter).parseRows(content);
      result = result.filter((x) => x.length >= result[0].length);

      if (result.length === 0) {
        return undefined;
      }

      const columnCount = result[0].length;

      // There needs to be at least 3 columns (northing, heighting and height)
      if (columnCount < options.minColumns) {
        return undefined;
      }

      // If there is only 1 column, ensure that this is the last delimiter to ensure all delimiters
      // were tested.
      if (columnCount === 1 && delimiter !== delimiters.at(-1)) {
        return undefined;
      }

      // The result needs to be uniform to be valid. (i.e. all rows have the same number of columns)
      if (!result.every((x) => x.length >= columnCount)) {
        return undefined;
      }

      return result;
    } catch {
      return undefined;
    }
  }

  for (const delimiter of delimiters) {
    const result = parseUniformCsv(delimiter);

    if (isDefined(result)) {
      return result;
    }
  }

  throw new Error(
    `Failed parsing CSV. The file needs to be delimited by either tab, semicolon or comma. And every row needs to have the same amount of columns.`,
  );
}

function parseCsvRowCoordinate(
  row: string[],
  northingRow: number,
  eastingRow: number,
  heightRow: number,
): Coordinates {
  const coordinate: Coordinates = {
    northing: Number(row[northingRow]?.replace(',', '.').replaceAll(' ', '')),
    easting: Number(row[eastingRow]?.replace(',', '.').replaceAll(' ', '')),
    height: Number(row[heightRow]?.replace(',', '.').replaceAll(' ', '')),
  };

  if (
    isValidNumber(coordinate.height) &&
    CoordinateManager.getInstance().isValidCoordinate(coordinate)
  ) {
    return coordinate;
  }

  throw new Error('Error parsing CSV row.');
}

const importPointsFromCsv = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.pointsCsv', { ns: 'skyviewDesign' }),
    action: () => {
      tool.activeFeature = DesignFeature.Import;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });
      tool.contextMenuCallback?.();
    },
  };
};

const applyCsvPoints = (
  tool: DesignTool,
  headers: string[],
  rows: string[][],
  northingRow: number,
  eastingRow: number,
  heightRow: number,
  createLineSegments: boolean,
) => {
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      changes = [];
      const ids: PointId[] = [];

      const headerAttributes: SkyMapAttribute[] = headers.map((x) => {
        const result = getEnumAttributeOrCreate(tool, x);
        if (isDefined(result.change)) {
          changes.unshift(result.change);
        }
        return result.attribute;
      });

      if (rows.length > 0 && tool.asset.designNode.pointNodeCount === 0) {
        const point = parseCsvRowCoordinate(rows[0], northingRow, eastingRow, heightRow);
        changes.unshift(repositionDesign(tool, point.easting, point.northing));
      }

      for (const row of rows) {
        const point = parseCsvRowCoordinate(row, northingRow, eastingRow, heightRow);

        const pointAttributes = new Map<AttributeId, AttributeData>();
        for (let i = 0; i < headerAttributes.length; i++) {
          const attribute = headerAttributes[i];
          const result = getAttributeDataOrAddValue(attribute, row[i]);

          if (isDefined(result)) {
            if (isDefined(result.change)) {
              changes.unshift(result.change);
            }

            pointAttributes.set(attribute.id, result.attributeData);
          }
        }

        const worldPosition = CoordinateManager.getInstance().projectCoordsToLocal(point);

        const result = tool.asset.designNode.addPoint(
          tool.asset.designNode.getLocalPosition(worldPosition),
          pointAttributes,
        );

        ids.push(result.id);
        changes.unshift(result);
      }

      if (createLineSegments) {
        changes.unshift(
          ...addLineSegmentsToDesignNode(
            tool.asset.designNode,
            ids.map((_x, idx) => (idx < ids.length - 1 ? [ids[idx], ids[idx + 1]] : [])).flat(),
          ),
        );
      }

      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
      publish(SubscriptionTopic.DesignAttributesChanged, undefined);
    },
    rollback: () => {
      tool.asset.designNode.revert(changes);
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
      publish(SubscriptionTopic.DesignAttributesChanged, undefined);
    },
  });
};

export const importFunctions = {
  importPointsFromCsv,
  applyCsvPoints,
  parseCsv,
  parseCsvRowCoordinate,
};
